import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ChevronIcon from "components/icons/chevron-icon";
import { COLORS } from "containers/impact-report/constants";
import {
  PaginationWrapper,
  PaginationButton,
  PageNumberButton,
  PageNumbersContainer,
  PaginationText,
  ButtonText
} from "./Pagination.styled";

const Pagination = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange
}) => {
  const { t } = useTranslation();
  const [backHovered, setBackHovered] = useState(false);
  const [nextHovered, setNextHovered] = useState(false);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePrevious = useCallback(() => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  }, [currentPage, onPageChange]);

  const handleNext = useCallback(() => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, onPageChange]);

  const handlePageClick = useCallback(
    pageNumber => {
      onPageChange(pageNumber);
    },
    [onPageChange]
  );

  const pageNumbers = useMemo(() => {
    const MAX_VISIBLE = 9;

    // Show all pages if total doesn't exceed max visible
    if (totalPages <= MAX_VISIBLE) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always render exactly MAX_VISIBLE buttons with active page centered
    const pagesPerSide = Math.floor(MAX_VISIBLE / 2);
    let startPage = Math.max(1, currentPage - pagesPerSide);
    let endPage = Math.min(totalPages, currentPage + pagesPerSide);

    // Adjust if we're near the start
    if (startPage === 1) {
      endPage = MAX_VISIBLE;
    }

    // Adjust if we're near the end
    if (endPage === totalPages) {
      startPage = Math.max(1, totalPages - MAX_VISIBLE + 1);
    }

    // Always return exactly MAX_VISIBLE page numbers
    return Array.from({ length: MAX_VISIBLE }, (_, i) => startPage + i);
  }, [totalPages, currentPage]);

  if (totalItems === 0) return null;

  const isBackDisabled = currentPage === 1;
  const isNextDisabled = currentPage === totalPages;

  return (
    <PaginationWrapper>
      <PaginationButton
        onClick={handlePrevious}
        disabled={isBackDisabled}
        onMouseEnter={() => setBackHovered(true)}
        onMouseLeave={() => setBackHovered(false)}
      >
        <ChevronIcon
          direction={"left"}
          size={24}
          color={
            isBackDisabled
              ? COLORS.DISABLED
              : backHovered
              ? COLORS.LINK_BLUE
              : COLORS.NEUTRAL_500
          }
          fillOpacity={"1"}
        />
        <ButtonText marginLeft>{t("impactReport.pagination.back")}</ButtonText>
      </PaginationButton>

      <PaginationText>
        {t("impactReport.pagination.pageInfo", {
          current: currentPage,
          total: totalPages
        })}
      </PaginationText>

      <PageNumbersContainer>
        {pageNumbers.map(num => (
          <PageNumberButton
            key={num}
            isActive={num === currentPage}
            onClick={() => handlePageClick(num)}
          >
            {num}
          </PageNumberButton>
        ))}
      </PageNumbersContainer>

      <PaginationButton
        onClick={handleNext}
        disabled={isNextDisabled}
        onMouseEnter={() => setNextHovered(true)}
        onMouseLeave={() => setNextHovered(false)}
      >
        <ButtonText marginRight>{t("impactReport.pagination.next")}</ButtonText>
        <ChevronIcon
          direction={"right"}
          size={24}
          color={
            isNextDisabled
              ? COLORS.DISABLED
              : nextHovered
              ? COLORS.LINK_BLUE
              : COLORS.NEUTRAL_500
          }
          fillOpacity={"1"}
        />
      </PaginationButton>
    </PaginationWrapper>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired
};

export default Pagination;
